import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Save, RefreshCw } from "lucide-react";
import { prepareMediaForUpload, formatBytes } from "../lib/mediaCompress";

// Petit composant utilitaire : évite les recalculs Object.entries/filter
// dans le corps JSX du parent (une massive .map()).
const ItemFields = React.memo(({ item }) => {
  const entries = React.useMemo(
    () => Object.entries(item).filter(([k]) => k !== "id" && k !== "receipt_token"),
    [item]
  );
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {entries.map(([k, v]) => (
        <div key={k}><b className="text-warm-orange">{k}:</b> {String(v).slice(0, 120)}</div>
      ))}
    </div>
  );
});

const isDev = process.env.NODE_ENV === "development";

const TABS = [
  { id: "stats", label: "Résultats & Pilote" },
  { id: "analytics", label: "Visiteurs" },
  { id: "newsletter", label: "Newsletter" },
  { id: "settings", label: "Paramètres du site" },
  { id: "users", label: "Utilisateurs", superOnly: true },
  { id: "audit", label: "Journal d'audit", superOnly: true },
  { id: "donations", label: "Dons" },
  { id: "volunteers", label: "Bénévoles" },
  { id: "partners", label: "Partenaires" },
  { id: "ambassadors", label: "Ambassadeurs" },
  { id: "contacts", label: "Contacts" },
  { id: "testimonials", label: "Témoignages" },
  { id: "founder", label: "Fondateur" },
  { id: "team", label: "Équipe" },
  { id: "gallery", label: "Galerie" },
  { id: "children", label: "Enfants à parrainer" },
  { id: "sponsorships", label: "Parrainages" },
  { id: "news", label: "Actualités" },
  { id: "actions", label: "Actions" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("stats");
  const [settings, setSettings] = useState({ stats:{}, pilot:{}, site:{} });
  const [lists, setLists] = useState({ donations:[], volunteers:[], partners:[], ambassadors:[], contacts:[], news:[], actions:[], testimonials:[] });
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [newsletter, setNewsletter] = useState([]);
  const isSuper = user?.role === "super_admin";

  const visibleTabs = TABS.filter(t => !t.superOnly || isSuper);

  const loadAll = useCallback(async () => {
    try {
      const s = await api.get("/settings"); setSettings(s.data);
      const [d,v,p,a,c,n,ac,tm,tem,gl,ch,sp] = await Promise.all([
        api.get("/admin/donations"), api.get("/admin/volunteers"), api.get("/admin/partners"),
        api.get("/admin/ambassadors"), api.get("/admin/contacts"), api.get("/news"), api.get("/actions"),
        api.get("/testimonials"), api.get("/team"), api.get("/gallery"),
        api.get("/children"), api.get("/admin/sponsorships"),
      ]);
      setLists({ donations:d.data, volunteers:v.data, partners:p.data, ambassadors:a.data, contacts:c.data, news:n.data, actions:ac.data, testimonials:tm.data, team:tem.data, gallery:gl.data, children:ch.data, sponsorships:sp.data });
      if (user?.role === "super_admin") {
        try { const u = await api.get("/admin/users"); setUsers(u.data); } catch (e) { if (isDev) console.error("Load users failed:", e); }
        try { const al = await api.get("/admin/audit-logs"); setAuditLogs(al.data); } catch (e) { if (isDev) console.error("Load audit failed:", e); }
      }
      try { const an = await api.get("/admin/analytics?days=30"); setAnalytics(an.data); } catch (e) { if (isDev) console.error("Load analytics failed:", e); }
      try { const nl = await api.get("/admin/newsletter"); setNewsletter(nl.data); } catch (e) { if (isDev) console.error("Load newsletter failed:", e); }
    } catch (e) {
      if (isDev) console.error("Erreur de chargement admin:", e);
      toast.error("Erreur de chargement");
    }
  }, [user?.role]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveKey = async (key) => {
    try { await api.put(`/settings/${key}`, { data: settings[key] }); toast.success("Enregistré"); }
    catch (e) { if (isDev) console.error("saveKey failed:", e); toast.error("Erreur d'enregistrement"); }
  };

  // Champs de settings qui doivent rester en string (URLs, coordonnées, IBAN…).
  // Les autres sont convertis en Number si possible.
  const STRING_FIELDS = new Set([
    "beneficiary", "paypal_link", "iban", "bic", "bank_name",
    "facebook", "instagram", "linkedin", "youtube",
  ]);
  const isStringField = (field) => (
    STRING_FIELDS.has(field)
    || field.includes("email")
    || field.includes("phone")
    || field.includes("address")
  );
  const coerceFieldValue = (field, val) => {
    if (isStringField(field)) return val;
    if (val === "") return "";
    return Number(val) || val;
  };
  const updateStat = (key, field, val) => setSettings(s => ({
    ...s,
    [key]: { ...s[key], [field]: coerceFieldValue(field, val) },
  }));

  const processMediaUpload = async (file, { videoMaxMb = 15, imageTargetKb = 1400 } = {}) => {
    if (!file) return { media_url: "", media_type: "" };
    try {
      const tId = toast.loading(file.type.startsWith("image/") ? "Optimisation de l'image…" : "Préparation du fichier…");
      const res = await prepareMediaForUpload(file, { videoMaxMb, imageTargetKb });
      toast.dismiss(tId);
      if (!res) return null;
      if (res.media_type === "image" && res.originalBytes > res.finalBytes * 1.1) {
        toast.success(`Photo optimisée : ${formatBytes(res.originalBytes)} → ${formatBytes(res.finalBytes)}`);
      }
      return { media_url: res.media_url, media_type: res.media_type };
    } catch (e) {
      toast.dismiss();
      toast.error(e?.message || "Impossible de lire le fichier");
      return null;
    }
  };

  const createNews = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const media = await processMediaUpload(e.target.media?.files?.[0]);
    if (media === null) return;
    try {
      await api.post("/news", { data: {
        title: fd.get("title"),
        content: fd.get("content"),
        category: fd.get("category"),
        date: fd.get("date"),
        media_url: media.media_url,
        media_type: media.media_type,
      }});
      e.target.reset(); loadAll(); toast.success("Actualité publiée");
    } catch (err) {
      const msg = err?.response?.status === 413
        ? "Image trop lourde pour le serveur. Réduisez à 2 Mo maximum."
        : (err?.response?.data?.detail || err?.message || "Erreur de publication");
      toast.error(msg);
    }
  };
  const createAction = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const media = await processMediaUpload(e.target.media?.files?.[0]);
    if (media === null) return;
    try {
      await api.post("/actions", { data: {
        title: fd.get("title"),
        description: fd.get("description"),
        location: fd.get("location"),
        date: fd.get("date"),
        beneficiaries: fd.get("beneficiaries"),
        media_url: media.media_url,
        media_type: media.media_type,
      }});
      e.target.reset(); loadAll(); toast.success("Action publiée");
    } catch (err) {
      const msg = err?.response?.status === 413
        ? "Média trop lourd pour le serveur. Réduisez la taille de l'image (≤2 Mo) ou de la vidéo (≤15 Mo)."
        : (err?.response?.data?.detail || err?.message || "Erreur de publication");
      toast.error(msg);
    }
  };

  const deleteItem = async (path, id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/${path}/${id}`); loadAll();
  };

  return (
    <div className="min-h-screen bg-pearl" data-testid="admin-dashboard">
      <header className="bg-deep-blue text-ivory">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-5 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold">Espace administrateur</div>
            <div className="font-display text-2xl mt-1">Larmes en Lumière</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-ivory/70">{user?.name} · <span className="text-gold">{user?.role}</span></span>
            <button onClick={()=>nav("/")} className="text-xs px-3 py-2 rounded-full bg-white/10 hover:bg-white/20" data-testid="admin-view-site">Voir le site</button>
            <button onClick={async()=>{ await logout(); nav("/admin/login"); }} className="text-xs px-3 py-2 rounded-full bg-gold text-deep-blue font-semibold inline-flex items-center gap-1" data-testid="admin-logout"><LogOut size={12}/> Déconnexion</button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 flex gap-1 overflow-x-auto pb-2">
          {visibleTabs.map(t => (
            <button key={t.id} onClick={()=>setTab(t.id)} className={`px-4 py-2 text-sm rounded-full whitespace-nowrap ${tab===t.id?"bg-gold text-deep-blue":"bg-white/10 text-ivory/80 hover:bg-white/20"}`} data-testid={`tab-${t.id}`}>{t.label}</button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-10 py-10">
        {tab === "stats" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-white">
              <h3 className="font-display text-2xl text-deep-blue mb-4">Statistiques publiques</h3>
              <div className="space-y-3">
                {Object.entries(settings.stats || {}).map(([k,v]) => (
                  <label key={k} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-deep-blue/70 capitalize">{k.replaceAll("_"," ")}</span>
                    <input type="number" value={v} onChange={e=>setSettings(s=>({...s, stats:{...s.stats, [k]: Number(e.target.value)||0}}))} className="w-32 p-2 rounded-lg border-2 border-black/5 focus:border-gold outline-none text-right" data-testid={`stat-${k}`}/>
                  </label>
                ))}
              </div>
              <button onClick={()=>saveKey("stats")} className="btn-gold mt-5 px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="save-stats"><Save size={14}/> Enregistrer</button>
            </div>
            <div className="p-6 rounded-2xl bg-white">
              <h3 className="font-display text-2xl text-deep-blue mb-4">Projet pilote — 100 Enfants 100 Sourires</h3>
              <div className="space-y-3">
                {Object.entries(settings.pilot || {}).map(([k,v]) => (
                  <label key={k} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-deep-blue/70 capitalize">{k.replaceAll("_"," ")}</span>
                    <input type="number" value={v} onChange={e=>setSettings(s=>({...s, pilot:{...s.pilot, [k]: Number(e.target.value)||0}}))} className="w-32 p-2 rounded-lg border-2 border-black/5 focus:border-gold outline-none text-right" data-testid={`pilot-${k}`}/>
                  </label>
                ))}
              </div>
              <button onClick={()=>saveKey("pilot")} className="btn-gold mt-5 px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="save-pilot"><Save size={14}/> Enregistrer</button>
            </div>
          </div>
        )}

        {tab === "settings" && (
          <div className="p-6 rounded-2xl bg-white max-w-3xl">
            <h3 className="font-display text-2xl text-deep-blue mb-4">Identité & logo</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div className="md:col-span-2 p-4 rounded-xl bg-pearl">
                <div className="flex items-center gap-4">
                  {settings.site?.logo_url ? (
                    <img src={settings.site.logo_url} alt="Aperçu" className="w-16 h-16 rounded-full object-cover border-2 border-gold"/>
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold to-warm-orange grid place-items-center font-display text-2xl text-deep-blue font-bold">L</div>
                  )}
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-deep-blue mb-2">Téléverser un logo (JPG, PNG, SVG)</label>
                    <input type="file" accept="image/*" data-testid="logo-upload"
                      onChange={(e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        if (f.size > 2 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 2 Mo)"); return; }
                        const reader = new FileReader();
                        reader.onload = () => setSettings(s => ({...s, site:{...s.site, logo_url: reader.result}}));
                        reader.readAsDataURL(f);
                      }} className="text-sm"/>
                    {settings.site?.logo_url && (
                      <button type="button" onClick={()=>setSettings(s=>({...s, site:{...s.site, logo_url:""}}))} className="ml-2 text-xs text-warm-orange underline" data-testid="logo-remove">Supprimer le logo</button>
                    )}
                    <div className="text-xs text-deep-blue/60 mt-1">Ou collez une URL d'image ci-dessous.</div>
                  </div>
                </div>
              </div>
              <label className="text-sm text-deep-blue/70 md:col-span-2">
                <div className="mb-1">URL du logo (alternative à l'upload)</div>
                <input value={settings.site?.logo_url ?? ""} onChange={e=>setSettings(s=>({...s, site:{...s.site, logo_url: e.target.value}}))} placeholder="https://…/mon-logo.png" className="w-full p-2.5 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="site-logo_url"/>
              </label>
              <label className="text-sm text-deep-blue/70">
                <div className="mb-1">Texte du logo (nom affiché)</div>
                <input value={settings.site?.logo_text ?? ""} onChange={e=>setSettings(s=>({...s, site:{...s.site, logo_text: e.target.value}}))} placeholder="Larmes en Lumière" className="w-full p-2.5 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="site-logo_text"/>
              </label>
              <label className="text-sm text-deep-blue/70">
                <div className="mb-1">Sous-titre du logo</div>
                <input value={settings.site?.logo_subtitle ?? ""} onChange={e=>setSettings(s=>({...s, site:{...s.site, logo_subtitle: e.target.value}}))} placeholder="Humanitaire" className="w-full p-2.5 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="site-logo_subtitle"/>
              </label>
              <label className="text-sm text-deep-blue/70 md:col-span-2">
                <div className="mb-1">📱 Numéro WhatsApp (format international, ex : +33612345678)</div>
                <input value={settings.site?.whatsapp_number ?? ""} onChange={e=>setSettings(s=>({...s, site:{...s.site, whatsapp_number: e.target.value}}))} placeholder="+33..." className="w-full p-2.5 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="site-whatsapp"/>
                <div className="text-xs text-deep-blue/60 mt-1">Laissez vide pour masquer le bouton flottant.</div>
              </label>
              <div className="md:col-span-2 p-4 rounded-xl bg-pearl">
                <div className="flex items-center gap-4">
                  {settings.site?.favicon_url ? (
                    <img src={settings.site.favicon_url} alt="Favicon" className="w-10 h-10 rounded border border-gold bg-white p-1"/>
                  ) : (
                    <div className="w-10 h-10 rounded bg-gold grid place-items-center text-deep-blue font-bold">F</div>
                  )}
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-deep-blue mb-2">Favicon (icône d'onglet, PNG/SVG carré recommandé)</label>
                    <input type="file" accept="image/*" data-testid="favicon-upload"
                      onChange={(e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        if (f.size > 512 * 1024) { toast.error("Favicon trop volumineux (max 512 Ko)"); return; }
                        const reader = new FileReader();
                        reader.onload = () => setSettings(s => ({...s, site:{...s.site, favicon_url: reader.result}}));
                        reader.readAsDataURL(f);
                      }} className="text-sm"/>
                    {settings.site?.favicon_url && (
                      <button type="button" onClick={()=>setSettings(s=>({...s, site:{...s.site, favicon_url:""}}))} className="ml-2 text-xs text-warm-orange underline" data-testid="favicon-remove">Supprimer</button>
                    )}
                  </div>
                </div>
              </div>
              <div className="md:col-span-2 p-4 rounded-xl bg-pearl">
                <div className="flex items-center gap-4">
                  {settings.site?.hero_image_url ? (
                    <img src={settings.site.hero_image_url} alt="Hero" className="w-24 h-16 rounded object-cover border-2 border-gold"/>
                  ) : (
                    <div className="w-24 h-16 rounded bg-gradient-to-br from-deep-blue to-warm-orange grid place-items-center text-ivory text-xs">Défaut</div>
                  )}
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-deep-blue mb-2">Bannière Hero (grande image d'accueil, max 5 Mo)</label>
                    <input type="file" accept="image/*" data-testid="hero-upload"
                      onChange={(e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        if (f.size > 5 * 1024 * 1024) { toast.error("Image trop volumineuse (max 5 Mo)"); return; }
                        const reader = new FileReader();
                        reader.onload = () => setSettings(s => ({...s, site:{...s.site, hero_image_url: reader.result}}));
                        reader.readAsDataURL(f);
                      }} className="text-sm"/>
                    {settings.site?.hero_image_url && (
                      <button type="button" onClick={()=>setSettings(s=>({...s, site:{...s.site, hero_image_url:""}}))} className="ml-2 text-xs text-warm-orange underline" data-testid="hero-remove">Rétablir l'image par défaut</button>
                    )}
                  </div>
                </div>
              </div>
              <div className="md:col-span-2 p-4 rounded-xl bg-pearl">
                <div className="flex items-center gap-4">
                  {settings.site?.hero_video_url ? (
                    <video src={settings.site.hero_video_url} muted className="w-24 h-16 rounded object-cover border-2 border-gold bg-black"/>
                  ) : (
                    <div className="w-24 h-16 rounded bg-deep-blue/20 grid place-items-center text-deep-blue/60 text-xs">Aucune vidéo</div>
                  )}
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-deep-blue mb-2">Vidéo Hero (facultatif, max 25 Mo — MP4 recommandé)</label>
                    <input type="file" accept="video/*" data-testid="hero-video-upload"
                      onChange={(e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        if (f.size > 25 * 1024 * 1024) { toast.error("Vidéo trop volumineuse (max 25 Mo)"); return; }
                        const reader = new FileReader();
                        reader.onload = () => setSettings(s => ({...s, site:{...s.site, hero_video_url: reader.result}}));
                        reader.readAsDataURL(f);
                      }} className="text-sm"/>
                    {settings.site?.hero_video_url && (
                      <button type="button" onClick={()=>setSettings(s=>({...s, site:{...s.site, hero_video_url:""}}))} className="ml-2 text-xs text-warm-orange underline" data-testid="hero-video-remove">Retirer la vidéo</button>
                    )}
                    <div className="text-xs text-deep-blue/60 mt-1">Quand une vidéo est présente, elle remplace l'image d'accueil (auto-play, muet, en boucle).</div>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="font-display text-2xl text-deep-blue mb-4 mt-8">Palette de couleurs</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-pearl">
                <div className="text-sm font-semibold text-deep-blue mb-2">Couleur principale (bleu profond)</div>
                <div className="flex items-center gap-3">
                  <input type="color" value={settings.site?.color_primary || "#0F2537"} onChange={e=>setSettings(s=>({...s, site:{...s.site, color_primary: e.target.value}}))} className="w-16 h-12 rounded-lg cursor-pointer border-2 border-white" data-testid="color-primary"/>
                  <input type="text" value={settings.site?.color_primary || "#0F2537"} onChange={e=>setSettings(s=>({...s, site:{...s.site, color_primary: e.target.value}}))} className="flex-1 p-2 rounded-lg border-2 border-black/5 focus:border-gold outline-none font-mono text-sm" data-testid="color-primary-hex"/>
                </div>
                <div className="text-xs text-deep-blue/60 mt-2">Utilisée pour les sections foncées et le texte principal.</div>
              </div>
              <div className="p-4 rounded-xl bg-pearl">
                <div className="text-sm font-semibold text-deep-blue mb-2">Couleur d'accent (or / lumière)</div>
                <div className="flex items-center gap-3">
                  <input type="color" value={settings.site?.color_accent || "#D4AF37"} onChange={e=>setSettings(s=>({...s, site:{...s.site, color_accent: e.target.value}}))} className="w-16 h-12 rounded-lg cursor-pointer border-2 border-white" data-testid="color-accent"/>
                  <input type="text" value={settings.site?.color_accent || "#D4AF37"} onChange={e=>setSettings(s=>({...s, site:{...s.site, color_accent: e.target.value}}))} className="flex-1 p-2 rounded-lg border-2 border-black/5 focus:border-gold outline-none font-mono text-sm" data-testid="color-accent-hex"/>
                </div>
                <div className="text-xs text-deep-blue/60 mt-2">Utilisée pour les boutons, chiffres clés et éléments dorés.</div>
              </div>
              <div className="md:col-span-2 flex items-center gap-2">
                <button type="button" onClick={()=>setSettings(s=>({...s, site:{...s.site, color_primary:"#0F2537", color_accent:"#D4AF37"}}))} className="text-xs text-warm-orange underline" data-testid="reset-colors">Réinitialiser les couleurs par défaut</button>
              </div>
            </div>
            <h3 className="font-display text-2xl text-deep-blue mb-4 mt-8">Coordonnées & réseaux</h3>
            <div className="p-5 rounded-2xl border-2 border-warm-orange/30 bg-warm-orange/5 mb-4" data-testid="paypal-panel">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="font-display text-xl text-deep-blue flex items-center gap-2">
                  💳 Lien PayPal des dons
                  {settings.site?.paypal_link
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-soft text-ivory">Configuré</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-warm-orange text-ivory">Non configuré</span>}
                </div>
                {settings.site?.paypal_link && (
                  <a href={settings.site.paypal_link} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 rounded-full border-2 border-deep-blue text-deep-blue font-semibold hover:bg-deep-blue hover:text-ivory transition" data-testid="paypal-test">
                    Tester le lien ↗
                  </a>
                )}
              </div>
              <input
                value={settings.site?.paypal_link ?? ""}
                onChange={e=>setSettings(s=>({...s, site:{...s.site, paypal_link: e.target.value}}))}
                placeholder="https://paypal.me/votrepseudo   ou   https://www.paypal.com/donate/?hosted_button_id=XXXXXXXX"
                className="w-full p-3 rounded-lg border-2 border-black/10 focus:border-gold outline-none font-mono text-sm"
                data-testid="site-paypal_link"
              />
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    const link = (settings.site?.paypal_link ?? "").trim();
                    try {
                      const tId = toast.loading("Enregistrement du lien PayPal…");
                      await api.put("/settings/site", { data: { paypal_link: link } });
                      // Recharge la source de vérité depuis l'API pour confirmer visuellement
                      const fresh = await api.get("/settings");
                      const persisted = fresh.data?.site?.paypal_link ?? "";
                      setSettings(s => ({ ...s, site: { ...s.site, paypal_link: persisted } }));
                      toast.dismiss(tId);
                      if (persisted) {
                        toast.success(`✅ Lien PayPal enregistré : ${persisted}`);
                      } else {
                        toast.success("Champ vidé — aucun lien PayPal enregistré.");
                      }
                      window.dispatchEvent(new Event("lel-settings-updated"));
                    } catch (e) {
                      toast.dismiss();
                      toast.error(`Erreur : ${e?.response?.data?.detail || e?.message || "sauvegarde impossible"}`);
                    }
                  }}
                  className="btn-gold px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2"
                  data-testid="save-paypal-only"
                >
                  <Save size={14}/> Enregistrer le lien PayPal
                </button>
                <span className="text-xs text-deep-blue/60 italic">Ce bouton n'enregistre <b>que</b> le lien PayPal — impossible d'écraser vos autres réglages par erreur.</span>
              </div>
              <p className="text-xs text-deep-blue/70 mt-3">
                Collez ici l'URL de votre bouton PayPal Donate <b>ou</b> votre lien PayPal.Me, puis cliquez sur le bouton doré ci-dessus.
              </p>
              <ul className="text-xs text-deep-blue/60 mt-2 list-disc pl-4 space-y-0.5">
                <li>PayPal.Me : le montant est ajouté automatiquement (ex : <code>/50EUR</code>).</li>
                <li>Bouton Donate PayPal : le montant est proposé par PayPal selon votre configuration.</li>
              </ul>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {["contact_email","contact_phone","address","beneficiary","iban","bic","bank_name","facebook","instagram","linkedin","youtube"].map(k => (
                <label key={k} className="text-sm text-deep-blue/70">
                  <div className="capitalize mb-1">{k.replaceAll("_"," ")}</div>
                  <input value={settings.site?.[k] ?? ""} onChange={e=>setSettings(s=>({...s, site:{...s.site, [k]: e.target.value}}))} className="w-full p-2.5 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid={`site-${k}`}/>
                </label>
              ))}
            </div>
            <button onClick={async ()=>{ await saveKey("site"); window.dispatchEvent(new Event("lel-settings-updated")); }} className="btn-gold mt-6 px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="save-site"><Save size={14}/> Enregistrer</button>
          </div>
        )}

        {["donations","volunteers","partners","ambassadors","contacts"].includes(tab) && (
          <div className="bg-white rounded-2xl p-6 overflow-x-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl text-deep-blue">{TABS.find(t=>t.id===tab)?.label} ({lists[tab]?.length || 0})</h3>
              <button onClick={loadAll} className="text-xs px-3 py-2 rounded-full bg-pearl inline-flex items-center gap-1"><RefreshCw size={12}/> Rafraîchir</button>
            </div>
            {(lists[tab] || []).length === 0 ? <p className="text-deep-blue/50 italic">Aucune donnée pour l'instant.</p> : (
              <div className="space-y-2">
                {lists[tab].map(item => (
                  <div key={item.id} className="p-4 rounded-xl bg-pearl text-sm text-deep-blue" data-testid={`${tab}-row-${item.id}`}>
                    <ItemFields item={item} />
                    {tab === "donations" && (
                      <div className="mt-3 flex flex-wrap gap-2 items-center">
                        {item.status === "confirmed" ? (
                          <>
                            <span className="text-xs px-2 py-1 rounded-full bg-green-soft/20 text-green-soft font-semibold">✓ Confirmé · {item.receipt_number}</span>
                            {item.receipt_token && (
                              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/receipts/${item.receipt_token}`} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1 rounded-full bg-deep-blue text-ivory inline-flex items-center gap-1" data-testid={`receipt-download-${item.id}`}>Télécharger le reçu PDF</a>
                            )}
                          </>
                        ) : (
                          <button
                            onClick={async()=>{
                              if (!window.confirm(`Confirmer le don de ${item.amount}€ de ${item.donor_name} et envoyer le reçu fiscal à ${item.donor_email} ?`)) return;
                              try {
                                const r = await api.put(`/admin/donations/${item.id}/confirm`);
                                toast.success(`Reçu ${r.data.receipt_number} envoyé`);
                                loadAll();
                              } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
                            }}
                            className="text-xs px-3 py-1 rounded-full bg-gold text-deep-blue font-semibold"
                            data-testid={`confirm-donation-${item.id}`}
                          >Confirmer & envoyer reçu fiscal</button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "analytics" && (
          <div className="space-y-6">
            {!analytics ? <p className="italic text-deep-blue/60">Chargement…</p> : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { l: "Visiteurs uniques (total)", v: analytics.total_unique_visitors, sub: "sessions distinctes" },
                    { l: "Pages vues (total)", v: analytics.total_views, sub: "depuis le lancement" },
                    { l: "Uniques aujourd'hui", v: analytics.today_unique, sub: "nouvelles sessions" },
                    { l: "Pages vues aujourd'hui", v: analytics.today_views, sub: "" },
                  ].map(c => (
                    <div key={c.l} className="p-5 rounded-2xl bg-white border-t-4 border-gold" data-testid={`analytics-${c.l}`}>
                      <div className="text-xs uppercase tracking-widest text-warm-orange">{c.l}</div>
                      <div className="font-display text-4xl text-deep-blue mt-2">{(c.v || 0).toLocaleString("fr-FR")}</div>
                      <div className="text-xs text-deep-blue/50 mt-1">{c.sub}</div>
                    </div>
                  ))}
                </div>
                <div className="grid lg:grid-cols-2 gap-6">
                  <div className="p-6 rounded-2xl bg-white">
                    <h3 className="font-display text-xl text-deep-blue mb-4">Trafic (30 derniers jours)</h3>
                    {(analytics.series || []).length === 0 ? <p className="italic text-deep-blue/50 text-sm">Aucune donnée sur cette période.</p> : (
                      <div className="space-y-1">
                        {analytics.series.map(row => {
                          const max = Math.max(...analytics.series.map(r => r.views), 1);
                          const pct = Math.round((row.views / max) * 100);
                          return (
                            <div key={row.day} className="flex items-center gap-3 text-xs">
                              <div className="w-20 text-deep-blue/60 font-mono">{row.day.slice(5)}</div>
                              <div className="flex-1 h-6 rounded bg-pearl relative overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-gold to-warm-orange" style={{width: `${pct}%`}}></div>
                                <div className="absolute inset-0 flex items-center px-2 text-deep-blue text-xs font-semibold">{row.views} vues · {row.unique} uniques</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="p-6 rounded-2xl bg-white">
                    <h3 className="font-display text-xl text-deep-blue mb-4">Pages les plus visitées</h3>
                    {(analytics.top_paths || []).length === 0 ? <p className="italic text-deep-blue/50 text-sm">Aucune donnée.</p> : (
                      <div className="space-y-2">
                        {analytics.top_paths.map((p, i) => (
                          <div key={p.path} className="flex items-center gap-3 p-2 rounded-lg bg-pearl">
                            <div className="text-xs font-mono text-warm-orange w-6">#{i+1}</div>
                            <div className="text-sm text-deep-blue flex-1 truncate">{p.path}</div>
                            <div className="text-sm font-semibold text-deep-blue">{p.views}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "newsletter" && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-display text-2xl text-deep-blue">Composer une newsletter</h3>
                  <p className="text-xs text-deep-blue/60">Envoyer un message unique à tous vos abonnés confirmés. Astuce : utilisez <code className="bg-pearl px-1">{'{{name}}'}</code> pour personnaliser.</p>
                </div>
              </div>
              <form onSubmit={async(e)=>{
                  e.preventDefault();
                  const fd = new FormData(e.target);
                  const subject = fd.get("subject")?.toString().trim();
                  const html_body = fd.get("html_body")?.toString().trim();
                  const only_confirmed = fd.get("only_confirmed") === "on";
                  const count = newsletter.filter(n => only_confirmed ? n.confirmed : true).length;
                  if (!subject || subject.length < 3) { toast.error("Sujet trop court"); return; }
                  if (!html_body || html_body.length < 10) { toast.error("Contenu trop court"); return; }
                  if (!window.confirm(`Envoyer « ${subject} » à ${count} abonné(s) ?`)) return;
                  try {
                    const r = await api.post("/admin/newsletter/send-digest", { subject, html_body, only_confirmed });
                    toast.success(`Envoyée à ${r.data.sent} abonné(s)${r.data.failed ? ` (${r.data.failed} échec)` : ""}`);
                    e.target.reset();
                  } catch (err) { toast.error(err?.response?.data?.detail || "Erreur d'envoi"); }
                }} className="space-y-3" data-testid="digest-form">
                <input name="subject" required minLength={3} maxLength={200} placeholder="Sujet de l'email (ex : Notre lumière du mois — Février)" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="digest-subject"/>
                <textarea name="html_body" required rows={8} placeholder={"Bonjour {{name}},\n\nVoici les nouvelles du mois…\n\n— L'équipe Larmes en Lumière"} className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none font-mono text-sm" data-testid="digest-body"/>
                <label className="flex items-center gap-2 text-sm text-deep-blue">
                  <input type="checkbox" name="only_confirmed" defaultChecked className="w-4 h-4" data-testid="digest-only-confirmed"/>
                  Envoyer uniquement aux abonnés confirmés ({newsletter.filter(n => n.confirmed).length})
                </label>
                <button className="btn-gold px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="digest-send">
                  📧 Envoyer maintenant
                </button>
                <p className="text-xs text-deep-blue/50 italic">Le HTML basique est autorisé (paragraphes, liens, titres). Chaque envoi est journalisé dans l'audit.</p>
              </form>
            </div>

            <div className="bg-white rounded-2xl p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-display text-2xl text-deep-blue">Abonnés ({newsletter.length})</h3>
                  <p className="text-xs text-deep-blue/60">Abonnés inscrits — <b className="text-green-soft">{newsletter.filter(n => n.confirmed).length} confirmés</b>, {newsletter.filter(n => !n.confirmed).length} en attente.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={async()=>{
                      try {
                        const r = await api.get("/admin/newsletter/export.csv?only_confirmed=true", { responseType: "blob" });
                        const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
                        const a = document.createElement("a"); a.href = url;
                        a.download = `newsletter-confirmed-${new Date().toISOString().slice(0,10)}.csv`;
                        document.body.appendChild(a); a.click(); a.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success("Export CSV téléchargé");
                      } catch { toast.error("Erreur export CSV"); }
                    }} className="btn-gold px-4 py-2 rounded-full text-sm font-semibold" data-testid="nl-export-confirmed">
                    Exporter CSV (confirmés)
                  </button>
                  <button onClick={async()=>{
                      try {
                        const r = await api.get("/admin/newsletter/export.csv?only_confirmed=false", { responseType: "blob" });
                        const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
                        const a = document.createElement("a"); a.href = url;
                        a.download = `newsletter-all-${new Date().toISOString().slice(0,10)}.csv`;
                        document.body.appendChild(a); a.click(); a.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success("Export CSV complet téléchargé");
                      } catch { toast.error("Erreur export CSV"); }
                    }} className="px-4 py-2 rounded-full text-sm font-semibold border-2 border-deep-blue text-deep-blue" data-testid="nl-export-all">
                    Exporter tous
                  </button>
                  <button onClick={loadAll} className="text-xs px-3 py-2 rounded-full bg-pearl inline-flex items-center gap-1" data-testid="nl-refresh"><RefreshCw size={12}/> Rafraîchir</button>
                </div>
              </div>
              {newsletter.length === 0 ? (
                <p className="italic text-deep-blue/50">Aucun abonné pour l'instant.</p>
              ) : (
                <div className="space-y-2">
                  {newsletter.map(n => (
                    <div key={n.id} className="p-4 rounded-xl bg-pearl flex flex-wrap items-center gap-3 text-sm text-deep-blue" data-testid={`nl-row-${n.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{n.email}
                          {n.confirmed ? <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-soft text-ivory">Confirmé</span> : <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-warm-orange text-ivory">En attente</span>}
                        </div>
                        <div className="text-xs text-deep-blue/60">{n.name || "—"} · {n.lang || "fr"} · inscrit le {(n.created_at || "").slice(0,10)}{n.confirmed_at ? ` · confirmé le ${(n.confirmed_at || "").slice(0,10)}` : ""}</div>
                      </div>
                      <button onClick={async()=>{
                          if (!window.confirm(`Supprimer l'abonné ${n.email} ?`)) return;
                          try {
                            await api.delete(`/admin/newsletter/${n.id}`);
                            const r = await api.get("/admin/newsletter"); setNewsletter(r.data);
                            toast.success("Abonné supprimé");
                          } catch { toast.error("Erreur"); }
                        }} className="text-xs px-3 py-1 rounded-full bg-warm-orange text-ivory" data-testid={`nl-delete-${n.id}`}>
                        Supprimer
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}


        {tab === "audit" && isSuper && (
          <div className="bg-white rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-2xl text-deep-blue">Journal d'audit ({auditLogs.length})</h3>
                <p className="text-xs text-deep-blue/60">Traces des actions sensibles : création, blocage, suppression d'utilisateurs, confirmations de dons.</p>
              </div>
              <button onClick={loadAll} className="text-xs px-3 py-2 rounded-full bg-pearl inline-flex items-center gap-1" data-testid="audit-refresh"><RefreshCw size={12}/> Rafraîchir</button>
            </div>
            {auditLogs.length === 0 ? <p className="italic text-deep-blue/50">Aucune action enregistrée.</p> : (
              <div className="space-y-2">
                {auditLogs.map(a => (
                  <div key={a.id} className="p-3 rounded-lg bg-pearl text-sm text-deep-blue" data-testid={`audit-row-${a.id}`}>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 items-center">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-deep-blue text-ivory font-mono">{a.action}</span>
                      <span className="text-xs text-deep-blue/60">{new Date(a.timestamp).toLocaleString("fr-FR")}</span>
                      <span className="text-xs"><b className="text-warm-orange">par</b> {a.actor_email} ({a.actor_role})</span>
                      <span className="text-xs"><b className="text-warm-orange">cible</b> {a.target_type}#{String(a.target_id).slice(0,12)}</span>
                    </div>
                    {a.details && Object.keys(a.details).length > 0 && (
                      <div className="mt-1 text-xs text-deep-blue/70">
                        {Object.entries(a.details).map(([k,v])=>(<span key={k} className="mr-3"><b>{k}:</b> {String(v)}</span>))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "founder" && (
          <div className="p-6 rounded-2xl bg-white max-w-3xl">
            <h3 className="font-display text-2xl text-deep-blue mb-4">Fondateur·rice</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <label className="text-sm text-deep-blue/70"><div className="mb-1">Nom</div>
                <input value={settings.site?.founder_name ?? ""} onChange={e=>setSettings(s=>({...s, site:{...s.site, founder_name:e.target.value}}))} className="w-full p-2.5 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="site-founder_name"/>
              </label>
              <label className="text-sm text-deep-blue/70"><div className="mb-1">Rôle / fonction</div>
                <input value={settings.site?.founder_role ?? ""} onChange={e=>setSettings(s=>({...s, site:{...s.site, founder_role:e.target.value}}))} placeholder="Fondateur · Larmes en Lumière" className="w-full p-2.5 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="site-founder_role"/>
              </label>
              <label className="text-sm text-deep-blue/70 md:col-span-2"><div className="mb-1">Citation phare</div>
                <input value={settings.site?.founder_quote ?? ""} onChange={e=>setSettings(s=>({...s, site:{...s.site, founder_quote:e.target.value}}))} className="w-full p-2.5 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="site-founder_quote"/>
              </label>
              <label className="text-sm text-deep-blue/70 md:col-span-2"><div className="mb-1">Biographie</div>
                <textarea rows={5} value={settings.site?.founder_bio ?? ""} onChange={e=>setSettings(s=>({...s, site:{...s.site, founder_bio:e.target.value}}))} className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="site-founder_bio"/>
              </label>
              <div className="md:col-span-2 p-4 rounded-xl bg-pearl flex items-center gap-4">
                {settings.site?.founder_photo ? <img src={settings.site.founder_photo} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-gold"/> : <div className="w-20 h-20 rounded-full bg-gold grid place-items-center text-deep-blue font-bold">F</div>}
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-deep-blue mb-2">Photo du fondateur (max 2 Mo)</label>
                  <input type="file" accept="image/*" data-testid="founder-photo-upload" onChange={(e)=>{ const f=e.target.files?.[0]; if(!f) return; if(f.size>2*1024*1024){toast.error("Fichier trop volumineux");return;} const r=new FileReader(); r.onload=()=>setSettings(s=>({...s, site:{...s.site, founder_photo:r.result}})); r.readAsDataURL(f); }} className="text-sm"/>
                  {settings.site?.founder_photo && <button type="button" onClick={()=>setSettings(s=>({...s, site:{...s.site, founder_photo:""}}))} className="ml-2 text-xs text-warm-orange underline">Supprimer</button>}
                </div>
              </div>
            </div>
            <button onClick={async ()=>{ await saveKey("site"); window.dispatchEvent(new Event("lel-settings-updated")); }} className="btn-gold mt-6 px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="save-founder"><Save size={14}/> Enregistrer</button>
          </div>
        )}

        {tab === "testimonials" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <form onSubmit={async(e)=>{e.preventDefault(); const fd=new FormData(e.target); const photo=e.target.photo.files?.[0]; let photoData=""; if(photo){ if(photo.size>2*1024*1024){toast.error("Photo trop lourde");return;} photoData=await new Promise(res=>{const r=new FileReader();r.onload=()=>res(r.result);r.readAsDataURL(photo);}); } await api.post("/testimonials",{data:{name:fd.get("name"),role:fd.get("role"),location:fd.get("location"),quote:fd.get("quote"),photo:photoData}}); e.target.reset(); loadAll(); toast.success("Témoignage ajouté"); }} className="p-6 rounded-2xl bg-white space-y-3">
              <h3 className="font-display text-xl text-deep-blue">Ajouter un témoignage</h3>
              <input name="name" required placeholder="Nom (ou prénom seul, avec accord)" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="tm-name"/>
              <input name="role" placeholder="Rôle (ex: Maman de deux enfants)" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="tm-role"/>
              <input name="location" placeholder="Lieu (ville, pays)" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="tm-loc"/>
              <textarea name="quote" required rows={4} placeholder="Témoignage" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="tm-quote"/>
              <input type="file" name="photo" accept="image/*" className="text-sm" data-testid="tm-photo"/>
              <button className="btn-gold px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="tm-submit">Publier</button>
              <p className="text-xs text-deep-blue/60 italic">Ne publiez que des témoignages avec l'accord écrit des personnes concernées.</p>
            </form>
            <div className="p-6 rounded-2xl bg-white space-y-3">
              <h3 className="font-display text-xl text-deep-blue">Témoignages publiés ({(lists.testimonials||[]).length})</h3>
              {(lists.testimonials||[]).map(tm=>(
                <div key={tm.id} className="p-3 rounded-lg bg-pearl flex justify-between items-start gap-3">
                  <div className="flex-1"><div className="font-semibold text-deep-blue">{tm.name} <span className="text-xs text-deep-blue/60 font-normal">— {tm.role}</span></div><div className="text-xs text-deep-blue/70 italic mt-1">« {tm.quote?.slice(0,120)}… »</div></div>
                  <button onClick={()=>deleteItem("testimonials",tm.id)} className="text-xs text-warm-orange">Supprimer</button>
                </div>
              ))}
              {(!lists.testimonials || !lists.testimonials.length) && <p className="italic text-deep-blue/50 text-sm">Aucun témoignage encore.</p>}
            </div>
          </div>
        )}

        {tab === "team" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <form onSubmit={async(e)=>{e.preventDefault();const fd=new FormData(e.target);const ph=e.target.photo.files?.[0];let pd="";if(ph){if(ph.size>2*1024*1024){toast.error("Photo trop lourde");return;}pd=await new Promise(res=>{const r=new FileReader();r.onload=()=>res(r.result);r.readAsDataURL(ph);});}await api.post("/team",{data:{name:fd.get("name"),role:fd.get("role"),bio:fd.get("bio"),photo:pd}});e.target.reset();loadAll();toast.success("Membre ajouté");}} className="p-6 rounded-2xl bg-white space-y-3">
              <h3 className="font-display text-xl text-deep-blue">Ajouter un membre d'équipe</h3>
              <input name="name" required placeholder="Nom complet" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="team-name"/>
              <input name="role" required placeholder="Fonction (ex: Coordinatrice terrain)" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="team-role"/>
              <textarea name="bio" rows={4} placeholder="Courte biographie" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="team-bio"/>
              <input type="file" name="photo" accept="image/*" className="text-sm" data-testid="team-photo"/>
              <button className="btn-gold px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="team-submit">Ajouter</button>
            </form>
            <div className="p-6 rounded-2xl bg-white space-y-3">
              <h3 className="font-display text-xl text-deep-blue">Membres ({(lists.team||[]).length})</h3>
              {(lists.team||[]).map(m=>(
                <div key={m.id} className="p-3 rounded-lg bg-pearl flex justify-between items-center gap-3">
                  <div className="flex items-center gap-3">{m.photo?<img src={m.photo} className="w-12 h-12 rounded-full object-cover" alt=""/>:<div className="w-12 h-12 rounded-full bg-gold grid place-items-center text-deep-blue font-bold">{m.name?.charAt(0)}</div>}<div><div className="font-semibold text-deep-blue">{m.name}</div><div className="text-xs text-deep-blue/60">{m.role}</div></div></div>
                  <button onClick={()=>deleteItem("team",m.id)} className="text-xs text-warm-orange">Supprimer</button>
                </div>
              ))}
              {(!lists.team||!lists.team.length)&&<p className="italic text-deep-blue/50 text-sm">Aucun membre ajouté.</p>}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <form onSubmit={async(e)=>{
                e.preventDefault();
                const fd = new FormData(e.target);
                const file = e.target.media?.files?.[0];
                if (!file) { toast.error("Fichier requis"); return; }
                const media = await processMediaUpload(file);
                if (!media || !media.media_url) return;
                try {
                  await api.post("/gallery", { data: {
                    media_url: media.media_url,
                    media_type: media.media_type,
                    image: media.media_type === "image" ? media.media_url : "", // legacy alias
                    caption: fd.get("caption") || "",
                    album: (fd.get("album") || "").trim(),
                  }});
                  e.target.reset(); loadAll(); toast.success(media.media_type === "video" ? "Vidéo ajoutée" : "Photo ajoutée");
                } catch (err) {
                  const msg = err?.response?.status === 413
                    ? "Média trop lourd pour le serveur. Réduisez-le avant l'envoi."
                    : (err?.response?.data?.detail || err?.message || "Erreur de publication");
                  toast.error(msg);
                }
              }} className="p-6 rounded-2xl bg-white space-y-3">
              <h3 className="font-display text-xl text-deep-blue">Ajouter photo ou vidéo</h3>
              <input type="file" name="media" accept="image/*,video/*" required className="text-sm" data-testid="gal-media"/>
              <input name="caption" placeholder="Légende (optionnelle)" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="gal-caption"/>
              <input name="album" list="album-list" placeholder="Album (mission, pays, événement…)" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="gal-album"/>
              <datalist id="album-list">
                {[...new Set((lists.gallery||[]).map(g => g.album).filter(Boolean))].map(a => (<option key={a} value={a}/>))}
              </datalist>
              <p className="text-xs text-deep-blue/60">Les photos sont <b>compressées automatiquement</b> avant l'envoi. Vidéos jusqu'à <b>15 Mo</b> (MP4 720p recommandé).</p>
              <button className="btn-gold px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="gal-submit">Publier</button>
            </form>
            <div className="p-6 rounded-2xl bg-white">
              <h3 className="font-display text-xl text-deep-blue mb-3">Photos & vidéos ({(lists.gallery||[]).length})</h3>
              <div className="grid grid-cols-3 gap-2">
                {(lists.gallery||[]).map(g=>{
                  const src = g.media_url || g.image;
                  const isVideo = g.media_type === "video";
                  return (
                    <div key={g.id} className="relative group aspect-square rounded overflow-hidden bg-black/5">
                      {isVideo
                        ? <video src={src} muted className="w-full h-full object-cover"/>
                        : <img src={src} alt="" className="w-full h-full object-cover"/>}
                      {isVideo && <span className="absolute top-1 left-1 text-[9px] px-1.5 py-0.5 rounded-full bg-warm-orange text-ivory">▶ vidéo</span>}
                      {g.album && <span className="absolute bottom-1 left-1 text-[9px] px-1.5 py-0.5 rounded-full bg-deep-blue/85 text-ivory">{g.album}</span>}
                      <button onClick={()=>deleteItem("gallery",g.id)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-warm-orange text-ivory text-xs opacity-0 group-hover:opacity-100">×</button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {tab === "children" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <form onSubmit={async(e)=>{e.preventDefault();const fd=new FormData(e.target);const ph=e.target.photo.files?.[0];let pd="";if(ph){if(ph.size>2*1024*1024){toast.error("Photo trop lourde");return;}pd=await new Promise(res=>{const r=new FileReader();r.onload=()=>res(r.result);r.readAsDataURL(ph);});}await api.post("/children",{data:{first_name:fd.get("first_name"),age:Number(fd.get("age")),location:fd.get("location"),story:fd.get("story"),monthly_amount:Number(fd.get("monthly_amount"))||30,photo:pd}});e.target.reset();loadAll();toast.success("Enfant ajouté");}} className="p-6 rounded-2xl bg-white space-y-3">
              <h3 className="font-display text-xl text-deep-blue">Ajouter un enfant à parrainer</h3>
              <input name="first_name" required placeholder="Prénom (ou pseudonyme)" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="ch-first"/>
              <div className="grid grid-cols-2 gap-3">
                <input name="age" type="number" min="0" required placeholder="Âge" className="p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="ch-age"/>
                <input name="monthly_amount" type="number" min="5" placeholder="Montant mensuel (€) — défaut 30" className="p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="ch-amount"/>
              </div>
              <input name="location" placeholder="Lieu (ville, pays)" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="ch-loc"/>
              <textarea name="story" rows={4} placeholder="Histoire courte (avec accord de la famille)" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="ch-story"/>
              <input type="file" name="photo" accept="image/*" className="text-sm" data-testid="ch-photo"/>
              <button className="btn-gold px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="ch-submit">Publier</button>
              <p className="text-xs text-deep-blue/60 italic">Ne publier qu'avec l'accord écrit des familles et le respect des règles de protection de l'enfance. Utilisez un prénom d'usage ou pseudonyme si nécessaire.</p>
            </form>
            <div className="p-6 rounded-2xl bg-white space-y-3">
              <h3 className="font-display text-xl text-deep-blue">Enfants publiés ({(lists.children||[]).length})</h3>
              {(lists.children||[]).map(c=>(
                <div key={c.id} className="p-3 rounded-lg bg-pearl flex justify-between items-center gap-3">
                  <div className="flex items-center gap-3">{c.photo?<img src={c.photo} className="w-12 h-12 rounded object-cover" alt=""/>:<div className="w-12 h-12 rounded bg-gold grid place-items-center text-deep-blue font-bold">{c.first_name?.charAt(0)}</div>}<div><div className="font-semibold text-deep-blue">{c.first_name} · {c.age} ans</div><div className="text-xs text-deep-blue/60">{c.location} — {c.monthly_amount}€/mois</div></div></div>
                  <button onClick={()=>deleteItem("children",c.id)} className="text-xs text-warm-orange">Supprimer</button>
                </div>
              ))}
              {(!lists.children||!lists.children.length)&&<p className="italic text-deep-blue/50 text-sm">Aucun enfant publié.</p>}
            </div>
          </div>
        )}

        {tab === "sponsorships" && (
          <div className="bg-white rounded-2xl p-6">
            <h3 className="font-display text-2xl text-deep-blue mb-4">Parrainages ({(lists.sponsorships||[]).length})</h3>
            {(lists.sponsorships||[]).length===0 ? <p className="italic text-deep-blue/50">Aucun parrainage.</p> : (
              <div className="space-y-2">{(lists.sponsorships||[]).map(sp=>(
                <div key={sp.id} className="p-4 rounded-xl bg-pearl text-sm text-deep-blue"><b className="text-warm-orange">{sp.sponsor_name}</b> — {sp.sponsor_email} · <b>{sp.amount}€/mois</b> · enfant: {sp.child_id} · <span className="text-xs">{sp.created_at}</span>{sp.message && <div className="text-xs italic mt-1">« {sp.message} »</div>}</div>
              ))}</div>
            )}
          </div>
        )}

        {tab === "news" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <form onSubmit={createNews} className="p-6 rounded-2xl bg-white space-y-3">
              <h3 className="font-display text-xl text-deep-blue">Publier une actualité</h3>
              <input name="title" required placeholder="Titre" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="news-title"/>
              <input name="category" placeholder="Catégorie" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="news-cat"/>
              <input name="date" type="date" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="news-date"/>
              <textarea name="content" rows={5} placeholder="Contenu" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="news-content"/>
              <label className="block text-sm text-deep-blue/70">
                <div className="mb-1 font-semibold text-deep-blue">Image ou vidéo (facultatif)</div>
                <input type="file" name="media" accept="image/*,video/*" className="text-sm" data-testid="news-media"/>
                <div className="text-xs text-deep-blue/60 mt-1">Image jusqu'à <b>2 Mo</b>, vidéo jusqu'à <b>15 Mo</b> (MP4 recommandé, résolution 720p).</div>
              </label>
              <button className="btn-gold px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="news-submit">Publier</button>
            </form>
            <div className="p-6 rounded-2xl bg-white space-y-3">
              <h3 className="font-display text-xl text-deep-blue">Actualités publiées</h3>
              {(lists.news||[]).map(n=>(
                <div key={n.id} className="p-3 rounded-lg bg-pearl flex justify-between items-start gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {n.media_url && n.media_type === "video" && <video src={n.media_url} className="w-16 h-12 rounded object-cover bg-black flex-shrink-0" muted/>}
                    {n.media_url && n.media_type !== "video" && <img src={n.media_url} alt="" className="w-16 h-12 rounded object-cover flex-shrink-0"/>}
                    <div className="min-w-0">
                      <div className="font-semibold text-deep-blue truncate">{n.title}</div>
                      <div className="text-xs text-deep-blue/60">{n.date} — {n.category}</div>
                    </div>
                  </div>
                  <button onClick={()=>deleteItem("news",n.id)} className="text-xs text-warm-orange flex-shrink-0">Supprimer</button>
                </div>
              ))}
              {(!lists.news || !lists.news.length) && <p className="italic text-deep-blue/50 text-sm">Aucune actualité publiée.</p>}
            </div>
          </div>
        )}

        {tab === "users" && isSuper && (
          <div className="grid lg:grid-cols-2 gap-6">
            <form
              onSubmit={async(e)=>{
                e.preventDefault();
                const fd = new FormData(e.target);
                try {
                  await api.post("/admin/users", {
                    email: fd.get("email"),
                    name: fd.get("name"),
                    password: fd.get("password"),
                    role: fd.get("role"),
                  });
                  e.target.reset();
                  const u = await api.get("/admin/users"); setUsers(u.data);
                  toast.success("Utilisateur créé");
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Erreur");
                }
              }}
              className="p-6 rounded-2xl bg-white space-y-3"
            >
              <h3 className="font-display text-xl text-deep-blue">Ajouter un utilisateur</h3>
              <p className="text-xs text-deep-blue/60">Seul le fondateur (super admin) peut créer, bloquer ou supprimer des comptes.</p>
              <input name="name" required placeholder="Nom complet" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="user-name"/>
              <input name="email" required type="email" placeholder="Email" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="user-email"/>
              <input name="password" required type="text" minLength={8} placeholder="Mot de passe (min. 8 caractères)" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="user-password"/>
              <select name="role" required defaultValue="admin" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="user-role">
                <option value="admin">Administrateur (accès complet contenu)</option>
                <option value="communication">Communication (actualités, galerie)</option>
                <option value="projets">Projets (programmes, actions, enfants)</option>
                <option value="financier">Financier (dons, transparence)</option>
                <option value="viewer">Lecture seule</option>
              </select>
              <button className="btn-gold px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="user-submit">Créer le compte</button>
            </form>

            <div className="p-6 rounded-2xl bg-white space-y-3">
              <h3 className="font-display text-xl text-deep-blue">Utilisateurs enregistrés ({users.length})</h3>
              {users.length === 0 && <p className="italic text-deep-blue/50 text-sm">Aucun utilisateur.</p>}
              {users.map(u => (
                <div key={u.user_id} className="p-3 rounded-lg bg-pearl" data-testid={`user-row-${u.user_id}`}>
                  <div className="flex justify-between items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-deep-blue truncate">
                        {u.name || u.email}
                        {u.is_founder && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gold text-deep-blue">Fondateur</span>}
                        {u.status === "blocked" && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-warm-orange text-ivory">Bloqué</span>}
                      </div>
                      <div className="text-xs text-deep-blue/60">{u.email} · <b className="text-warm-orange">{u.role}</b></div>
                    </div>
                    {!u.is_founder && (
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={async()=>{
                            const next = u.status === "blocked" ? "active" : "blocked";
                            try {
                              await api.put(`/admin/users/${u.user_id}`, { status: next });
                              const r = await api.get("/admin/users"); setUsers(r.data);
                              toast.success(next === "blocked" ? "Compte bloqué" : "Compte réactivé");
                            } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
                          }}
                          className="text-xs px-3 py-1 rounded-full bg-deep-blue text-ivory"
                          data-testid={`user-toggle-${u.user_id}`}
                        >{u.status === "blocked" ? "Réactiver" : "Bloquer"}</button>
                        <button
                          onClick={async()=>{
                            const pw = window.prompt("Nouveau mot de passe (min. 8 caractères)");
                            if (!pw || pw.length < 8) { toast.error("Mot de passe trop court"); return; }
                            try {
                              await api.put(`/admin/users/${u.user_id}`, { password: pw });
                              toast.success("Mot de passe réinitialisé");
                            } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
                          }}
                          className="text-xs px-3 py-1 rounded-full bg-white border border-deep-blue text-deep-blue"
                          data-testid={`user-reset-${u.user_id}`}
                        >Reset MDP</button>
                        <button
                          onClick={async()=>{
                            if (!window.confirm(`Supprimer définitivement ${u.email} ?`)) return;
                            try {
                              await api.delete(`/admin/users/${u.user_id}`);
                              const r = await api.get("/admin/users"); setUsers(r.data);
                              toast.success("Utilisateur supprimé");
                            } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
                          }}
                          className="text-xs px-3 py-1 rounded-full bg-warm-orange text-ivory"
                          data-testid={`user-delete-${u.user_id}`}
                        >Supprimer</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "actions" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <form onSubmit={createAction} className="p-6 rounded-2xl bg-white space-y-3">
              <h3 className="font-display text-xl text-deep-blue">Publier une action</h3>
              <input name="title" required placeholder="Titre" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="act-title"/>
              <input name="location" placeholder="Lieu" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="act-loc"/>
              <input name="date" type="date" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="act-date"/>
              <input name="beneficiaries" type="number" placeholder="Nombre de bénéficiaires" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="act-benef"/>
              <textarea name="description" rows={5} placeholder="Description" className="w-full p-3 rounded-lg border-2 border-black/5 focus:border-gold outline-none" data-testid="act-desc"/>
              <label className="block text-sm text-deep-blue/70">
                <div className="mb-1 font-semibold text-deep-blue">Image ou vidéo (facultatif)</div>
                <input type="file" name="media" accept="image/*,video/*" className="text-sm" data-testid="act-media"/>
                <div className="text-xs text-deep-blue/60 mt-1">Image jusqu'à <b>2 Mo</b>, vidéo jusqu'à <b>15 Mo</b> (MP4 recommandé, résolution 720p).</div>
              </label>
              <button className="btn-gold px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="act-submit">Publier</button>
            </form>
            <div className="p-6 rounded-2xl bg-white space-y-3">
              <h3 className="font-display text-xl text-deep-blue">Actions publiées</h3>
              {(lists.actions||[]).map(n=>(
                <div key={n.id} className="p-3 rounded-lg bg-pearl flex justify-between items-start gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {n.media_url && n.media_type === "video" && <video src={n.media_url} className="w-16 h-12 rounded object-cover bg-black flex-shrink-0" muted/>}
                    {n.media_url && n.media_type !== "video" && <img src={n.media_url} alt="" className="w-16 h-12 rounded object-cover flex-shrink-0"/>}
                    <div className="min-w-0">
                      <div className="font-semibold text-deep-blue truncate">{n.title}</div>
                      <div className="text-xs text-deep-blue/60">{n.date} — {n.location}</div>
                    </div>
                  </div>
                  <button onClick={()=>deleteItem("actions",n.id)} className="text-xs text-warm-orange flex-shrink-0">Supprimer</button>
                </div>
              ))}
              {(!lists.actions || !lists.actions.length) && <p className="italic text-deep-blue/50 text-sm">Aucune action publiée.</p>}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
